import { supabase } from '@/plugins/supabase'

/**
 * 技术分类 API
 */
export const categoryApi = {
  // 获取分类树
  async getCategoryTree() {
    const { data, error } = await supabase
      .from('tech_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
    
    if (error) throw error
    
    // 构建树形结构
    const parentCategories = data.filter(cat => !cat.parent_id)
    return parentCategories.map(parent => ({
      ...parent,
      children: data.filter(child => child.parent_id === parent.id)
    }))
  },
  
  // 根据 slug 获取分类
  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('tech_categories')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single()
    
    if (error) throw error
    return data
  }
}

/**
 * 文章 API
 */
export const articleApi = {
  // 获取文章列表（分页）
  async getArticles({ page = 1, pageSize = 10, categoryId = null, keyword = null }) {
    let query = supabase
      .from('articles')
      .select('*, tech_categories(*)', { count: 'exact' })
      .eq('is_published', true)
      .order('published_at', { ascending: false })
    
    if (categoryId) {
      query = query.eq('category_id', categoryId)
    }
    
    if (keyword) {
      query = query.or(`title.ilike.%${keyword}%,summary.ilike.%${keyword}%`)
    }
    
    const from = (page - 1) * pageSize
    const to = from + pageSize - 1
    
    const { data, error, count } = await query.range(from, to)
    
    if (error) throw error
    
    return {
      list: data,
      total: count,
      page,
      pageSize
    }
  },
  
  // 根据 slug 获取文章详情
  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('articles')
      .select('*, tech_categories(*)')
      .eq('slug', slug)
      .eq('is_published', true)
      .single()
    
    if (error) throw error
    
    // 获取文章标签
    const { data: tags } = await supabase
      .from('article_tags')
      .select('tags(*)')
      .eq('article_id', data.id)
    
    data.tags = tags?.map(t => t.tags) || []
    
    return data
  },
  
  // 获取精选文章
  async getFeatured(limit = 6) {
    const { data, error } = await supabase
      .from('articles')
      .select('*, tech_categories(*)')
      .eq('is_published', true)
      .eq('is_featured', true)
      .order('published_at', { ascending: false })
      .limit(limit)
    
    if (error) throw error
    return data
  },
  
  // 增加浏览量
  async incrementViewCount(id) {
    const { data: article } = await supabase
      .from('articles')
      .select('view_count')
      .eq('id', id)
      .single()
    
    if (article) {
      await supabase
        .from('articles')
        .update({ view_count: (article.view_count || 0) + 1 })
        .eq('id', id)
    }
  },
  
  // 获取相关文章
  async getRelated(articleId, categoryId, limit = 4) {
    const { data, error } = await supabase
      .from('articles')
      .select('*, tech_categories(*)')
      .eq('is_published', true)
      .eq('category_id', categoryId)
      .neq('id', articleId)
      .order('published_at', { ascending: false })
      .limit(limit)
    
    if (error) throw error
    return data
  }
}

/**
 * 标签 API
 */
export const tagApi = {
  // 获取所有标签
  async getAll() {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .order('name', { ascending: true })
    
    if (error) throw error
    return data
  }
}

/**
 * 资源导航 API
 */
export const resourceApi = {
  // 获取资源列表
  async getResources() {
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
    
    if (error) throw error
    
    // 按分类分组
    const grouped = {}
    data.forEach(resource => {
      if (!grouped[resource.category]) {
        grouped[resource.category] = []
      }
      grouped[resource.category].push(resource)
    })
    
    return grouped
  }
}

/**
 * 轮播图 API
 */
export const bannerApi = {
  // 获取轮播图列表
  async getBanners() {
    const { data, error } = await supabase
      .from('banners')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
    
    if (error) throw error
    return data
  }
}

/**
 * 网站配置 API
 */
export const settingApi = {
  // 获取所有配置
  async getSettings() {
    const { data, error } = await supabase
      .from('site_settings')
      .select('*')
    
    if (error) throw error
    
    // 转换为键值对
    const settings = {}
    data.forEach(item => {
      settings[item.key] = item.value
    })
    
    return settings
  }
}

/**
 * 评论 API
 */
export const commentApi = {
  // 获取文章评论
  async getComments(articleId) {
    const { data, error } = await supabase
      .from('comments')
      .select('*')
      .eq('article_id', articleId)
      .eq('is_approved', true)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },
  
  // 提交评论
  async submit(comment) {
    const { data, error } = await supabase
      .from('comments')
      .insert([{
        article_id: comment.articleId,
        content: comment.content,
        author_name: comment.authorName,
        author_email: comment.authorEmail,
        parent_id: comment.parentId || null,
        is_approved: false // 需要审核
      }])
    
    if (error) throw error
    return data
  }
}
